import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import PageBackground from '../components/common/PageBackground.js';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {PlayArrow, Pause, ThumbUp, ThumbDown, Replay, Delete, Flag} from '@mui/icons-material';
import {drawLine} from '../utils/drawLine.js';
import {RECORD_SIZE, decodeStrokes as decodeStrokesRaw} from '../utils/strokes.js';
import './GalleryPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const W = 980;
const H = 600;

/**
 * Parse the binary layer container format:
 *   [nameLen:u16][name:utf8][dataLen:u32][data:bytes]...
 * Returns Map<layerName, ArrayBuffer>
 */
function parseLayers(buffer) {
    const view = new DataView(buffer);
    const layers = new Map();
    const layerOrder = [];
    let offset = 0;
    while (offset + 6 <= buffer.byteLength) {
        const nameLen = view.getUint16(offset, true);
        const dataLen = view.getUint32(offset + 2, true);
        offset += 6;
        if (offset + nameLen + dataLen > buffer.byteLength) break;
        const nameBytes = new Uint8Array(buffer, offset, nameLen);
        const name = new TextDecoder().decode(nameBytes);
        offset += nameLen;
        const data = buffer.slice(offset, offset + dataLen);
        offset += dataLen;
        layers.set(name, data);
        layerOrder.push(name);
    }
    return {layers, layerOrder};
}

export default function GalleryPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const [drawing, setDrawing] = useState(null);
    const [error, setError] = useState(null);
    const [allStrokes, setAllStrokes] = useState([]); // flat array of {l, ...stroke}
    const [layerOrder, setLayerOrder] = useState([]);
    const [totalStrokes, setTotalStrokes] = useState(0);

    // Playback state
    const [position, setPosition] = useState(0); // 0..totalStrokes
    const [playing, setPlaying] = useState(false);
    const playingRef = useRef(false);
    const positionRef = useRef(0);

    // Vote state
    const [votes, setVotes] = useState({up: 0, down: 0});
    const [myVote, setMyVote] = useState(0);
    const [voteLoading, setVoteLoading] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [reported, setReported] = useState(false);

    // Canvas refs — one per layer
    const canvasContainerRef = useRef(null);
    const canvasesRef = useRef(new Map()); // layerName → canvas element
    const renderedPosRef = useRef(0); // tracks last rendered position for incremental drawing

    // Scale — fit both width and height so everything stays on screen
    const [windowSize, setWindowSize] = useState({w: window.innerWidth, h: window.innerHeight});
    useEffect(() => {
        const onResize = () => setWindowSize({w: window.innerWidth, h: window.innerHeight});
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // Reserve space for header (~48), controls (~52), meta (~80), gaps (~40)
    const verticalChrome = 220;
    const scale = useMemo(() => {
        const sw = (windowSize.w - 40) / W;
        const sh = (windowSize.h - verticalChrome) / H;
        return Math.min(1, Math.max(0.15, Math.min(sw, sh)));
    }, [windowSize]);

    // Fetch metadata
    useEffect(() => {
        fetch(`${API_URL}/api/gallery/${encodeURIComponent(id)}`)
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(data => {
                setDrawing(data);
                setVotes(data.votes || {up: 0, down: 0});
                setMyVote(data.myVote || 0);
                setIsOwner(!!data.isOwner);
                setReported(!!data.reported);
            })
            .catch(e => setError(e === 404 ? 'not_found' : 'error'));
    }, [id]);

    // Fetch strokes
    useEffect(() => {
        fetch(`${API_URL}/api/gallery/${encodeURIComponent(id)}/strokes`)
            .then(r => r.ok ? r.arrayBuffer() : Promise.reject(r.status))
            .then(buf => {
                const {layers, layerOrder: order} = parseLayers(buf);
                setLayerOrder(order);
                // Decode each layer and flatten into ordered array with layer tag
                const flat = [];
                for (const name of order) {
                    const ab = layers.get(name);
                    if (!ab || ab.byteLength === 0) continue;
                    const decoded = decodeStrokesRaw(ab);
                    for (const s of decoded) {
                        flat.push({l: name, ...s});
                    }
                }
                flat.sort((a, b) => a.sid - b.sid);
                setAllStrokes(flat);
                setTotalStrokes(flat.length);
                setPosition(flat.length); // start fully drawn
            })
            .catch(() => {});
    }, [id]);

    // Create canvas elements for each layer
    useEffect(() => {
        if (layerOrder.length === 0) return;
        const container = canvasContainerRef.current;
        if (!container) return;
        // Clear old canvases
        container.innerHTML = '';
        const newMap = new Map();
        const len = layerOrder.length;
        layerOrder.forEach((name, index) => {
            const c = document.createElement('canvas');
            c.width = W;
            c.height = H;
            c.style.position = 'absolute';
            c.style.top = '0';
            c.style.left = '0';
            c.style.width = '100%';
            c.style.height = '100%';
            c.style.zIndex = (len - index);
            container.appendChild(c);
            newMap.set(name, c);
        });
        canvasesRef.current = newMap;
        renderedPosRef.current = 0; // reset so next render does a full draw
    }, [layerOrder]);

    // Full redraw from scratch up to `pos`
    const fullRedraw = useCallback((pos) => {
        const canvases = canvasesRef.current;
        if (canvases.size === 0) return;
        for (const [, c] of canvases) {
            const ctx = c.getContext('2d');
            ctx.clearRect(0, 0, W, H);
        }
        for (let i = 0; i < pos && i < allStrokes.length; i++) {
            const s = allStrokes[i];
            drawLine(canvases, s, s.t);
        }
        renderedPosRef.current = pos;
    }, [allStrokes]);

    // Incremental render: only draw new strokes when moving forward,
    // full redraw when seeking backward or to 0.
    useEffect(() => {
        const canvases = canvasesRef.current;
        if (canvases.size === 0) return;
        const prev = renderedPosRef.current;
        if (position <= 0) {
            // Clear everything
            for (const [, c] of canvases) {
                const ctx = c.getContext('2d');
                ctx.clearRect(0, 0, W, H);
            }
            renderedPosRef.current = 0;
        } else if (position > prev) {
            // Forward: draw only new strokes
            for (let i = prev; i < position && i < allStrokes.length; i++) {
                const s = allStrokes[i];
                drawLine(canvases, s, s.t);
            }
            renderedPosRef.current = position;
        } else if (position < prev) {
            // Backward seek: full redraw required
            fullRedraw(position);
        }
        // position === prev: no-op
    }, [position, allStrokes, fullRedraw]);

    // Playback animation loop
    useEffect(() => {
        playingRef.current = playing;
    }, [playing]);

    useEffect(() => {
        positionRef.current = position;
    }, [position]);

    useEffect(() => {
        if (!playing) return;
        // Strokes per frame — scale speed with total stroke count
        const batchSize = Math.max(1, Math.ceil(totalStrokes / 300));
        const interval = setInterval(() => {
            if (!playingRef.current) return;
            setPosition(prev => {
                const next = Math.min(prev + batchSize, totalStrokes);
                if (next >= totalStrokes) {
                    setPlaying(false);
                }
                return next;
            });
        }, 16);
        return () => clearInterval(interval);
    }, [playing, totalStrokes]);

    // Vote handler
    const handleVote = async (value) => {
        if (voteLoading) return;
        setVoteLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/gallery/${encodeURIComponent(id)}/vote`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({value}),
            });
            if (res.ok) {
                const data = await res.json();
                setVotes(data.votes);
                setMyVote(data.myVote);
            }
        } catch (e) { /* ignore */ }
        setVoteLoading(false);
    };

    const handleDelete = async () => {
        if (deleting) return;
        if (!window.confirm(t('gallery.confirmDelete', 'Delete this drawing? This cannot be undone.'))) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API_URL}/api/gallery/${encodeURIComponent(id)}`, {method: 'DELETE'});
            if (res.ok) {
                navigate('/');
                return;
            }
        } catch (e) { /* ignore */ }
        setDeleting(false);
    };

    const handleReport = async () => {
        if (reported) return;
        if (!window.confirm(t('gallery.confirmReport', 'Report this drawing as inappropriate?'))) return;
        try {
            const res = await fetch(`${API_URL}/api/gallery/${encodeURIComponent(id)}/report`, {method: 'POST'});
            if (res.ok) setReported(true);
        } catch (e) { /* ignore */ }
    };

    const handleReplay = () => {
        setPosition(0);
        setPlaying(true);
    };

    const handleSlider = (e) => {
        const val = Number(e.target.value);
        setPosition(val);
        setPlaying(false);
    };

    if (error === 'not_found') {
        return (
            <div className="GalleryPage">
                <PageBackground />
                <div className="gallery-error">
                    <h2>{t('gallery.notFound', 'Drawing not found')}</h2>
                    <button className="gallery-home-btn" onClick={() => navigate('/')}>
                        {t('gallery.backHome', 'Back to Home')}
                    </button>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="GalleryPage">
                <PageBackground />
                <div className="gallery-error">
                    <h2>{t('gallery.error', 'Something went wrong')}</h2>
                </div>
            </div>
        );
    }

    const typeLabel = drawing?.type === 'guess'
        ? t('gallery.guessRound', 'Guess Round')
        : t('gallery.replayDrawing', 'Replay Drawing');

    return (
        <div className="GalleryPage">
            <PageBackground />
            <div className="gallery-header" style={{width: W * scale, maxWidth: '100%'}}>
                <span
                    className="gallery-header-home"
                    onClick={() => navigate('/')}
                    style={{
                        cursor: 'pointer', color: '#bbc', fontFamily: 'monospace',
                        fontSize: 13, fontWeight: 700, letterSpacing: 1,
                        userSelect: 'none',
                    }}
                    title={t('gallery.backHome', 'Back to Home')}
                >suttu.io</span>
                <h1 className="gallery-title">{typeLabel}</h1>
            </div>

            <div className="gallery-content">
                {/* Canvas area */}
                <div className="gallery-canvas-wrap" style={{width: W * scale, height: H * scale}}>
                    <div className="gallery-canvas-bg"
                         ref={canvasContainerRef}
                         style={{width: W, height: H, transform: `scale(${scale})`, transformOrigin: 'top left'}}
                    />
                </div>

                {/* Playback controls */}
                <div className="gallery-controls" style={{width: W * scale}}>
                    <Tooltip title={playing ? t('gallery.pause', 'Pause') : t('gallery.play', 'Play')}>
                        <IconButton onClick={() => {
                            if (position >= totalStrokes) {
                                setPosition(0);
                            }
                            setPlaying(p => !p);
                        }} style={{color: '#fff'}}>
                            {playing ? <Pause /> : <PlayArrow />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('gallery.replay', 'Replay from start')}>
                        <IconButton onClick={handleReplay} style={{color: '#aab'}}>
                            <Replay />
                        </IconButton>
                    </Tooltip>
                    <input
                        type="range"
                        className="gallery-scrubber"
                        min={0}
                        max={totalStrokes}
                        value={position}
                        onChange={handleSlider}
                    />
                    <span className="gallery-stroke-count">
                        {position} / {totalStrokes}
                    </span>
                </div>

                {/* Metadata + votes panel */}
                <div className="gallery-meta" style={{maxWidth: W * scale}}>
                    {drawing && (
                        <>
                            {drawing.drawerName && (
                                <div className="gallery-meta-row">
                                    <span className="gallery-meta-label">{t('gallery.drawnBy', 'Drawn by')}</span>
                                    <span className="gallery-meta-value">{drawing.drawerName}</span>
                                </div>
                            )}
                            {drawing.word && (
                                <div className="gallery-meta-row">
                                    <span className="gallery-meta-label">{t('gallery.word', 'Word')}</span>
                                    <span className="gallery-meta-value">{drawing.word}</span>
                                </div>
                            )}
                            <div className="gallery-meta-row">
                                <span className="gallery-meta-label">{t('gallery.savedBy', 'Saved by')}</span>
                                <span className="gallery-meta-value">{drawing.savedBy}</span>
                            </div>
                            <div className="gallery-meta-row">
                                <span className="gallery-meta-label">{t('gallery.date', 'Date')}</span>
                                <span className="gallery-meta-value">
                                    {new Date(drawing.createdAt).toLocaleDateString(undefined, {
                                        year: 'numeric', month: 'short', day: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                    })}
                                </span>
                            </div>
                            {drawing.metadata?.roundNumber > 0 && (
                                <div className="gallery-meta-row">
                                    <span className="gallery-meta-label">{t('gallery.round', 'Round')}</span>
                                    <span className="gallery-meta-value">{drawing.metadata.roundNumber}</span>
                                </div>
                            )}
                        </>
                    )}

                    {/* Votes */}
                    <div className="gallery-votes">
                        <Tooltip title={t('gallery.upvote', 'Upvote')}>
                            <IconButton
                                onClick={() => handleVote(1)}
                                disabled={voteLoading}
                                style={{color: myVote === 1 ? '#4caf50' : '#889'}}
                            >
                                <ThumbUp />
                            </IconButton>
                        </Tooltip>
                        <span className="gallery-vote-count">{votes.up}</span>
                        <Tooltip title={t('gallery.downvote', 'Downvote')}>
                            <IconButton
                                onClick={() => handleVote(-1)}
                                disabled={voteLoading}
                                style={{color: myVote === -1 ? '#f44336' : '#889'}}
                            >
                                <ThumbDown />
                            </IconButton>
                        </Tooltip>
                        <span className="gallery-vote-count">{votes.down}</span>
                        {isOwner && (
                            <Tooltip title={t('gallery.delete', 'Delete drawing')}>
                                <IconButton
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    style={{color: '#f44336', marginLeft: 'auto'}}
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!isOwner && (
                            <Tooltip title={reported ? t('gallery.reported', 'Reported') : t('gallery.report', 'Report')}>
                                <IconButton
                                    onClick={handleReport}
                                    disabled={reported}
                                    style={{color: reported ? '#f9a825' : '#667', marginLeft: isOwner ? 0 : 'auto'}}
                                >
                                    <Flag fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
